import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UsersGetProfileResponse, UsersSignUpDataDto } from 'types/swagger';
import { mergeSignupState } from './signUpHooks';
import {
  AccessInfo,
  AntiFraudType,
  ContactsData,
  DocumentData,
  ExtraInformation,
  MaximalData,
  PersonalInformation,
  PrivacyConsentsData,
  PromoData,
  RegistrationFromPage,
  SignupState,
  SpidType,
} from './types';
import { activateContract } from './actions';
import { ApiStatus } from 'features/api/thunkUtils';
import { isMatch, isNotEmpty } from 'utility/functions';

import format from 'date-fns/format';
import parse from 'date-fns/parse';

export const initialSignupState: SignupState = {
  cap: '',
  city: '',
  name: '',
  email: '',
  isSpid: false,
  pinCode: '',
  surname: '',
  address: '',
  province: '',
  username: '',
  password: '',
  promoCode: '',
  birthDate: '',
  telephone: '',
  countryCode: '',
  releasedBy: '',
  fiscalCode: '',
  cityOfBirth: '',
  domicileCap: '',
  releaseDate: '',
  contractCode: '',
  domicileCity: '',
  secretAnswer: '',
  documentType: '',
  releasePlace: '',
  antiFraudCode: '',
  weeklyMaximal: '',
  documentNumber: '',
  expirationDate: '',
  secretQuestion: '',
  acceptContract: false,
  profilingUsage: undefined,
  marketingUsage: undefined,
  provinceOfBirth: '',
  domicileAddress: '',
  isExternalState: false,
  domicileProvince: '',
  isSameAsDomicile: true,
  privacyInformation: false,
  isFiscalCodeCalculated: false,
  acceptTermAndCondition: false,
  isExternalResidencial: false,
  registrationFromPage: RegistrationFromPage.REGISTRATION,
  triggerForm: false,
  activatingApiStaus: ApiStatus.uninitialized,
};

export type UnregisterModal = {
  feedbackUnregistration: string;
};

const reduceContacts = (state, payload: ContactsData): void => {
  state.email = payload.email;
  state.telephone = payload.telephone;
  state.countryCode = payload.countryCode;
};
const reducePersonalData = (state, payload: PersonalInformation): void => {
  state.name = payload.name;
  state.surname = payload.surname;
  state.birthDate = payload.birthDate;
  state.gender = payload.gender;
  state.provinceOfBirth = payload.provinceOfBirth;
  state.cityOfBirth = payload.cityOfBirth;
  state.fiscalCode = payload.fiscalCode;
  state.address = payload.address;
  state.province = payload.province;
  state.city = payload.city;
  state.cap = payload.cap;
  state.domicileAddress = payload.domicileAddress;
  state.domicileCap = payload.domicileCap;
  state.domicileCity = payload.domicileCity;
  state.domicileProvince = payload.domicileProvince;
};
const reduceDocumentData = (state, payload: DocumentData): void => {
  state.documentType = payload.documentType;
  state.documentNumber = payload.documentNumber;
  state.releasedBy = payload.releasedBy;
  state.releasePlace = payload.releasePlace;
  state.releaseDate = payload.releaseDate;
  state.expirationDate = payload.expirationDate;
};
const reduceContractAccount = (state, payload: { contractCode: string; pincode: string }): void => {
  state.pinCode = payload.pincode;
  state.contractCode = payload.contractCode;
};

const signupSlice = createSlice({
  name: 'Signup',
  initialState: initialSignupState,
  reducers: {
    setContacts: (state, action: PayloadAction<ContactsData>) => reduceContacts(state, action.payload),
    setDocumentData: (state, action: PayloadAction<DocumentData>) => reduceDocumentData(state, action.payload),
    setPersonalData: (state, action: PayloadAction<PersonalInformation>) => reducePersonalData(state, action.payload),
    setContractAccount: (state, action: PayloadAction<{ contractCode: string; pincode: string }>) =>
      reduceContractAccount(state, action.payload),
    setAccessData: (state, action: PayloadAction<AccessInfo>) => {
      state.username = action.payload.username;
      state.password = action.payload.password;
      state.secretAnswer = action.payload.secretAnswer;
      state.secretQuestion = action.payload.secretQuestion;
    },
    setPrivacyConsents: (state, action: PayloadAction<PrivacyConsentsData>) => {
      state.acceptContract = action.payload.acceptContract;
      state.profilingUsage = action.payload.profilingUsage;
      state.marketingUsage = action.payload.marketingUsage;
      state.privacyInformation = action.payload.privacyInformation;
      state.acceptTermAndCondition = action.payload.acceptTermAndCondition;
    },
    setEmailSessionID: (state, action: PayloadAction<ContactsData['userSessionEmail']>) => {
      state.userSessionEmail = action.payload;
    },
    setTelephoneSessionID: (state, action: PayloadAction<ContactsData['userSessionTelephone']>) => {
      state.userSessionTelephone = action.payload;
    },
    setPromoCode: (state, action: PayloadAction<PromoData>) => {
      state.promoCode = action.payload.promoCode;
    },
    setMaximal: (state, action: PayloadAction<MaximalData>) => {
      state.weeklyMaximal = action.payload.weeklyMaximal;
    },
    setIsSpid: (state, action: PayloadAction<SpidType>) => {
      state.isSpid = action.payload.isSpid;
      state.spidCode = action.payload.spidCode;
    },
    setIsSameAsResidence: (state, { payload }: PayloadAction<boolean>) => {
      state.isSameAsDomicile = payload;
    },
    setIsCalculatedFiscalCode: (state, { payload }: PayloadAction<boolean>) => {
      state.isFiscalCodeCalculated = payload;
    },
    setAntiFraudCode: (state, { payload: code }: PayloadAction<AntiFraudType['antiFraudCode']>) => {
      state.antiFraudCode = code;
    },
    setIsExternalState: (state, { payload: isExternalState }: PayloadAction<boolean>) => {
      state.isExternalState = isExternalState;
    },
    setSignup: (state, { payload }: PayloadAction<Partial<UsersSignUpDataDto>>) => {
      mergeSignupState(state, payload);
    },
    setExtraData: (state, { payload }: PayloadAction<ExtraInformation>) => {
      state.btag = payload.btag;
      state.provenienceCode = payload.provenienceCode;
    },
    setFiscalCodeSignup: (state, { payload }: PayloadAction<string>) => {
      state.fiscalCode = payload;
    },
    setIsExternalResidencial: (state, { payload: isExternalResidencial }: PayloadAction<boolean>) => {
      state.isExternalResidencial = isExternalResidencial;
    },
    setRegistrationFromPage: (state, { payload }: PayloadAction<RegistrationFromPage>) => {
      state.registrationFromPage = payload;
    },
    setTriggerForm: (state, { payload }: PayloadAction<boolean>) => {
      state.triggerForm = payload;
    },
    resetSignUp: () => initialSignupState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(activateContract.pending.type, (state) => {
        delete state.activatingError;
        state.activatingApiStaus = ApiStatus.loading;
      })
      .addCase(activateContract.fulfilled.type, (state, { meta, payload }: AnyAction) => {
        delete state.activatingError;

        const data = payload as UsersGetProfileResponse;

        if (isMatch(`${data?.esito}`, '^543$')) {
          state.activatingError = data?.descrizione ?? 'Generic error';
        } else {
          // setContractAccount
          reduceContractAccount(state, meta.arg);

          // setPersonalData
          reducePersonalData(state, {
            cap: data?.cap ?? '',
            name: data?.nome ?? '',
            city: data?.comune ?? '',
            gender: data?.sesso as any,
            surname: data?.cognome ?? '',
            address: data?.indirizzo ?? '',
            province: data?.provincia ?? '',
            birthDate: (data?.datanasc ?? '').substring(0, 10),
            fiscalCode: data?.codfisc ?? '',
            cityOfBirth: data?.citta ?? '',
            provinceOfBirth: data?.provincia_nasc ?? '',
            isExternalState: false,
            isSameAsDomicile: true,
          });

          // setContacts
          reduceContacts(state, {
            email: data?.email ?? '',
            telephone: data?.telefono_cell ?? '',
          });

          // setDocumentData
          let releaseDate = '';
          if (data.data_docYY && data.data_docMM && data.data_docDD) {
            releaseDate = [data.data_docYY, data.data_docMM, data.data_docDD].join('-');
          } else if (data?.data_doc && isNotEmpty(data?.data_doc)) {
            releaseDate = format(parse(data.data_doc ?? '', 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');
          }

          let expirationDate = '';
          if (data?.data_sca_docYY && data?.data_sca_docMM && data?.data_sca_docDD) {
            expirationDate = [data?.data_sca_docYY, data?.data_sca_docMM, data?.data_sca_docDD].join('-');
          } else if (data?.data_sca_doc) {
            expirationDate = format(parse(data.data_sca_doc ?? '', 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');
          }
          reduceDocumentData(state, {
            releasedBy: data?.doc_ril_da ?? '',
            releasePlace: data?.luogo_doc ?? '',
            documentType: data?.cod_tipo_doc ?? '',
            documentNumber: data?.num_doc ?? '',
            releaseDate,
            expirationDate,
          });
        }

        state.activatingApiStaus = ApiStatus.idle;
      })
      .addCase(activateContract.rejected.type, (state) => {
        state.activatingError = 'Generic error';
        state.activatingApiStaus = ApiStatus.failed;
      });
  },
});

export const {
  setIsSpid,
  setSignup,
  setMaximal,
  setContacts,
  resetSignUp,
  setExtraData,
  setPromoCode,
  setAccessData,
  setTriggerForm,
  setPersonalData,
  setDocumentData,
  setAntiFraudCode,
  setEmailSessionID,
  setIsExternalState,
  setPrivacyConsents,
  setContractAccount,
  setFiscalCodeSignup,
  setIsSameAsResidence,
  setTelephoneSessionID,
  setRegistrationFromPage,
  setIsExternalResidencial,
  setIsCalculatedFiscalCode,
} = signupSlice.actions;

export default signupSlice.reducer;
